import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  geoPlaceCellKey,
  resolveGeoPlaceLabelResult,
} from './directoryGeoPlace.js';

export interface DirectoryGeoCoordItem {
  readonly lat: number | null;
  readonly lng: number | null;
}

export interface UseDirectoryGeoPlaceLabelsOptions {
  /** i18n language forwarded to Nominatim Accept-Language. */
  readonly acceptLanguage?: string | null;
}

const EMPTY_PLACE_LABELS: ReadonlyMap<string, string> = new Map();

/** Nominatim ~1 req/s — wait at least this long between retryable attempts. */
export const DIRECTORY_GEO_PLACE_RETRY_DELAY_MS = 1100;
/** Max reverse attempts per cell per fingerprint (includes first try). */
export const DIRECTORY_GEO_PLACE_MAX_ATTEMPTS = 3;

function buildCellTargets(
  items: readonly DirectoryGeoCoordItem[],
): Map<string, { lat: number; lng: number }> {
  const map = new Map<string, { lat: number; lng: number }>();
  for (const item of items) {
    if (
      typeof item.lat !== 'number' ||
      typeof item.lng !== 'number' ||
      !Number.isFinite(item.lat) ||
      !Number.isFinite(item.lng)
    ) {
      continue;
    }
    const key = geoPlaceCellKey(item.lat, item.lng);
    if (!map.has(key)) {
      map.set(key, { lat: item.lat, lng: item.lng });
    }
  }
  return map;
}

function fingerprintCellKeys(fingerprint: string): string[] {
  if (fingerprint.length === 0) {
    return [];
  }
  // Fingerprint may include `#lang` suffix from acceptLanguage.
  const hashIdx = fingerprint.indexOf('#');
  const cellsPart = hashIdx >= 0 ? fingerprint.slice(0, hashIdx) : fingerprint;
  if (cellsPart.length === 0) {
    return [];
  }
  return cellsPart.split('|').filter((key) => key.length > 0);
}

function fingerprintLanguage(fingerprint: string): string {
  const hashIdx = fingerprint.indexOf('#');
  return hashIdx >= 0 ? fingerprint.slice(hashIdx + 1) : '';
}

/** Cell-key subset with matching accept-language (or both empty lang). */
function isReusableFingerprintPrefix(priorFp: string, currentFp: string): boolean {
  if (fingerprintLanguage(priorFp) !== fingerprintLanguage(currentFp)) {
    return false;
  }
  const subsetKeys = fingerprintCellKeys(priorFp);
  if (subsetKeys.length === 0) {
    return fingerprintCellKeys(currentFp).length === 0;
  }
  const ofKeys = new Set(fingerprintCellKeys(currentFp));
  for (const key of subsetKeys) {
    if (!ofKeys.has(key)) {
      return false;
    }
  }
  return true;
}

function overlapLabels(
  fingerprint: string,
  labels: ReadonlyMap<string, string>,
): ReadonlyMap<string, string> {
  if (fingerprint.length === 0 || labels.size === 0) {
    return EMPTY_PLACE_LABELS;
  }
  const overlap = new Map<string, string>();
  for (const key of fingerprintCellKeys(fingerprint)) {
    const label = labels.get(key);
    if (typeof label === 'string' && label.length > 0) {
      overlap.set(key, label);
    }
  }
  return overlap.size > 0 ? overlap : EMPTY_PLACE_LABELS;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/**
 * When `enabled`, resolve city/village/place labels for unique shop coord cells.
 * Returns a map keyed by {@link geoPlaceCellKey}.
 *
 * Stable cell fingerprint drives resolution — `items` identity churn
 * (infinite-scroll / refetch) does not cancel in-flight work. Fingerprint
 * settles even when all cells resolve to null.
 *
 * G2: retryable reverse outcomes re-attempt with Nominatim-friendly backoff
 * (does not permanently abandon the cell for the current fingerprint).
 */
export function useDirectoryGeoPlaceLabels(
  items: readonly DirectoryGeoCoordItem[],
  enabled: boolean,
  options?: UseDirectoryGeoPlaceLabelsOptions,
): ReadonlyMap<string, string> {
  const acceptLanguage =
    typeof options?.acceptLanguage === 'string' ? options.acceptLanguage.trim() : '';

  const cellTargets = useMemo(
    () => (enabled ? buildCellTargets(items) : new Map<string, { lat: number; lng: number }>()),
    [enabled, items],
  );

  const cellKeyFingerprint = useMemo(() => {
    const cells = Array.from(cellTargets.keys()).sort().join('|');
    if (cells.length === 0) {
      return '';
    }
    return acceptLanguage.length > 0 ? `${cells}#${acceptLanguage}` : cells;
  }, [acceptLanguage, cellTargets]);

  const [resolved, setResolved] = useState<{
    readonly fingerprint: string;
    readonly labels: ReadonlyMap<string, string>;
  }>(() => ({ fingerprint: '', labels: EMPTY_PLACE_LABELS }));

  const cellTargetsRef = useRef(cellTargets);
  const resolvedRef = useRef(resolved);

  // Sync refs outside render (eslint react-hooks/refs).
  useLayoutEffect(() => {
    cellTargetsRef.current = cellTargets;
    resolvedRef.current = resolved;
  });

  useEffect(() => {
    if (!enabled || cellKeyFingerprint.length === 0) {
      return;
    }

    const fingerprint = cellKeyFingerprint;
    const langOpt = acceptLanguage.length > 0 ? acceptLanguage : null;
    let cancelled = false;
    const next = new Map<string, string>();
    const prior = resolvedRef.current;

    // Seed overlapping prior labels only when language matches (list growth).
    if (isReusableFingerprintPrefix(prior.fingerprint, fingerprint)) {
      for (const key of fingerprintCellKeys(fingerprint)) {
        const label = prior.labels.get(key);
        if (typeof label === 'string' && label.length > 0) {
          next.set(key, label);
        }
      }
    }

    const run = async (): Promise<void> => {
      const targets = cellTargetsRef.current;

      // Publish seeded overlap after a microtask (avoid sync setState in effect).
      if (next.size > 0) {
        await Promise.resolve();
        if (cancelled) {
          return;
        }
        setResolved({
          fingerprint,
          labels: new Map(next),
        });
      }

      for (const key of fingerprintCellKeys(fingerprint)) {
        if (cancelled) {
          return;
        }
        if (next.has(key)) {
          continue;
        }
        const coords = targets.get(key);
        if (coords === undefined) {
          continue;
        }

        let attempts = 0;
        while (attempts < DIRECTORY_GEO_PLACE_MAX_ATTEMPTS) {
          if (cancelled) {
            return;
          }
          attempts += 1;
          const result = await resolveGeoPlaceLabelResult(coords.lat, coords.lng, {
            acceptLanguage: langOpt,
          });
          if (cancelled) {
            return;
          }
          if (result.kind === 'label') {
            next.set(key, result.label);
            setResolved({
              fingerprint,
              labels: new Map(next),
            });
            break;
          }
          if (result.kind === 'empty') {
            // Sticky empty — stop; do not burn retry budget.
            break;
          }
          // retryable — backoff then try again while budget remains
          if (attempts >= DIRECTORY_GEO_PLACE_MAX_ATTEMPTS) {
            break;
          }
          await delay(DIRECTORY_GEO_PLACE_RETRY_DELAY_MS);
        }
      }

      // Always settle fingerprint — even when next is empty / only nulls.
      if (!cancelled) {
        setResolved({
          fingerprint,
          labels: new Map(next),
        });
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [acceptLanguage, cellKeyFingerprint, enabled]);

  if (!enabled || cellKeyFingerprint.length === 0) {
    return EMPTY_PLACE_LABELS;
  }
  if (resolved.fingerprint === cellKeyFingerprint) {
    return resolved.labels;
  }
  // Fingerprint growth / subset (same language): keep prior labels while new cells resolve.
  if (
    resolved.fingerprint.length > 0 &&
    isReusableFingerprintPrefix(resolved.fingerprint, cellKeyFingerprint)
  ) {
    return overlapLabels(cellKeyFingerprint, resolved.labels);
  }
  return EMPTY_PLACE_LABELS;
}
